/**
 * Proyecto: MOGOTAX
 * Módulo TAI - Vista de Detalle de Transportadora
 * Fecha: 18 de Febrero 2026
 */

import React from "react";
import {
  Modal,
  Descriptions,
  Tag,
  Divider,
  Typography,
  Space,
  Button,
} from "antd";
import PropTypes from "prop-types";
import {
  CarOutlined,
  MailOutlined,
  PhoneOutlined,
  HomeOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
} from "@ant-design/icons";

const { Title } = Typography;

const ViewTransportadoraDialog = ({ transportadora, open, handleClose }) => {
  if (!transportadora) return null;

  const getTipoColor = (tipo) => {
    const colors = {
      bus: "blue",
      taxi: "gold",
      especial: "purple",
      mixto: "green",
    };
    return colors[tipo] || "default";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("es-CO", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatContractDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("es-CO", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={
        <div
          style={{
            background: "linear-gradient(90deg, #0a2e5c, #004d99)",
            padding: "12px",
            borderRadius: "8px 8px 0 0",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            color: "white",
            fontSize: "20px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <CarOutlined style={{ marginRight: "8px" }} />
          Detalle de Transportadora
        </div>
      }
      footer={[
        <Button key="close" type="primary" onClick={handleClose}>
          Cerrar
        </Button>,
      ]}
      width={800}
    >
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Información General */}
        <div>
          <Title level={5}>
            <CarOutlined /> Información General
          </Title>
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Nombre" span={2}>
              <Space>
                <strong>{transportadora.nombre}</strong>
                {transportadora.es_mogotax_especial && (
                  <Tag color="orange">Mogotax Especial</Tag>
                )}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="NIT">
              {transportadora.nit || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Razón Social">
              {transportadora.razon_social || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Tipo de Transporte">
              <Tag color={getTipoColor(transportadora.tipo_transporte)}>
                {transportadora.tipo_transporte?.charAt(0).toUpperCase() +
                  transportadora.tipo_transporte?.slice(1)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Estado">
              {transportadora.estado === "activo" ? (
                <Tag icon={<CheckCircleOutlined />} color="success">
                  Activo
                </Tag>
              ) : (
                <Tag icon={<CloseCircleOutlined />} color="error">
                  Inactivo
                </Tag>
              )}
            </Descriptions.Item>
          </Descriptions>
        </div>

        <Divider style={{ margin: "16px 0" }} />

        {/* Información de Contacto */}
        <div>
          <Title level={5}>
            <PhoneOutlined /> Información de Contacto
          </Title>
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item
              label={
                <>
                  <HomeOutlined /> Dirección
                </>
              }
              span={2}
            >
              {transportadora.direccion || "-"}
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <>
                  <PhoneOutlined /> Teléfono
                </>
              }
            >
              {transportadora.telefono || "-"}
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <>
                  <MailOutlined /> Email
                </>
              }
            >
              {transportadora.email || "-"}
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <>
                  <EnvironmentOutlined /> Zona
                </>
              }
            >
              {transportadora.zona?.nombre || "Sin zona asignada"}
            </Descriptions.Item>
          </Descriptions>
        </div>

        <Divider style={{ margin: "16px 0" }} />

        {/* Persona de Contacto Principal */}
        <div>
          <Title level={5}>
            <UserOutlined /> Persona de Contacto Principal
          </Title>
          <Descriptions bordered column={3} size="small">
            <Descriptions.Item label="Nombre">
              {transportadora.contacto_nombre || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Teléfono">
              {transportadora.contacto_telefono || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Cargo">
              {transportadora.contacto_cargo || "-"}
            </Descriptions.Item>
          </Descriptions>
        </div>

        {/* Persona de Contacto Adicional */}
        {(transportadora.contacto2_nombre || transportadora.contacto2_telefono || transportadora.contacto2_cargo) && (
          <>
            <Divider style={{ margin: "16px 0" }} />
            <div>
              <Title level={5}>
                <UserOutlined /> Persona de Contacto Adicional
              </Title>
              <Descriptions bordered column={3} size="small">
                <Descriptions.Item label="Nombre">
                  {transportadora.contacto2_nombre || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Teléfono">
                  {transportadora.contacto2_telefono || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Cargo">
                  {transportadora.contacto2_cargo || "-"}
                </Descriptions.Item>
              </Descriptions>
            </div>
          </>
        )}

        {/* Observaciones */}
        {transportadora.observaciones && (
          <>
            <Divider style={{ margin: "16px 0" }} />
            <div>
              <Title level={5}>Observaciones</Title>
              <div style={{
                background: "#f5f5f5",
                padding: "12px",
                borderRadius: "6px",
                whiteSpace: "pre-wrap"
              }}>
                {transportadora.observaciones}
              </div>
            </div>
          </>
        )}

        <Divider style={{ margin: "16px 0" }} />

        {/* Fechas de Contrato */}
        <div>
          <Title level={5}>
            <CalendarOutlined /> Fechas de Contrato
          </Title>
          <Descriptions bordered column={3} size="small">
            <Descriptions.Item label="Fecha de Inicio">
              {formatContractDate(transportadora.contrato_fecha_inicio)}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha de Fin">
              {transportadora.contrato_indefinido
                ? <Tag color="blue">Indefinido</Tag>
                : formatContractDate(transportadora.contrato_fecha_fin)}
            </Descriptions.Item>
            <Descriptions.Item label="Estado del Contrato">
              {transportadora.contrato_indefinido ? (
                <Tag color="green">Vigente (Indefinido)</Tag>
              ) : transportadora.contrato_fecha_fin ? (
                new Date(transportadora.contrato_fecha_fin) >= new Date() ? (
                  <Tag color="green">Vigente</Tag>
                ) : (
                  <Tag color="red">Vencido</Tag>
                )
              ) : (
                <Tag color="default">Sin definir</Tag>
              )}
            </Descriptions.Item>
          </Descriptions>
        </div>

        <Divider style={{ margin: "16px 0" }} />

        {/* Información del Sistema */}
        <div>
          <Title level={5}>Información del Sistema</Title>
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="ID">
              {transportadora.id}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha de Creación">
              {formatDate(transportadora.created_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Última Actualización">
              {formatDate(transportadora.updated_at)}
            </Descriptions.Item>
          </Descriptions>
        </div>
      </Space>
    </Modal>
  );
};

ViewTransportadoraDialog.propTypes = {
  transportadora: PropTypes.object,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default ViewTransportadoraDialog;
